import React, { useEffect, useState } from 'react';
import { Label } from '@atoms/index';
import { RadioButton } from '@atoms/RadioButton/RadioButton';
import { InlineErrorMessage } from '@atoms/Errors/InlineErrorMessage';
import { ErrorMessages } from '@types';
import { renderTabIndex } from '@utils';

export type RadioButtonOptionsProps = {
  id?: string;
  name?: string;
  isDefault?: boolean;
  labelText: string;
  subtext?: string;
  buttonRef?:
    | React.RefObject<HTMLInputElement>
    | React.MutableRefObject<HTMLInputElement>
    | null;
};

type SingleRadioButtonProps = RadioButtonOptionsProps & {
  radio: string;
  setRadio: (value: string) => void;
  onChange: (result: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  radioIndex: number;
};

export interface RadioOptionsProps {
  title?: string;
  isLabelTextTitle?: boolean;
  description?: string;
  className?: string;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  onChange: (result: string) => void;
  options: RadioButtonOptionsProps[];
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const SingleRadioButton: React.FC<SingleRadioButtonProps> = ({
  id,
  name,
  labelText,
  subtext,
  buttonRef,
  radio,
  setRadio,
  onChange,
  onKeyDown,
  radioIndex,
}) => {
  return (
    <div className="mb-3 flex flex-col justify-center">
      <div className="flex items-center">
        <RadioButton
          id={id}
          ref={buttonRef}
          name={name}
          value={labelText}
          checked={radio === labelText}
          onChange={() => {
            setRadio(labelText);
            onChange(labelText);
          }}
          onKeyDown={onKeyDown}
          tabIndex={renderTabIndex(radio, labelText, radioIndex)}
        />
        <div
          className={`flex flex-col justify-center pl-3 ${subtext ? 'pt-6' : ''} text-start`}
        >
          <div className="text-lg">
            <Label
              className={`!text-brand-primary text-bodyLg mb-0 inline-block cursor-pointer select-none`}
              htmlFor={id}
              text={labelText}
            />
          </div>
          {subtext ? (
            <div className={'mt-1 max-w-lg'}>
              <pre className="body-3 max-w-lg whitespace-pre-wrap">
                {subtext}
              </pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export function RadioOptions(props: RadioOptionsProps) {
  const defaultText = props.options.find(
    (option) => option.isDefault,
  )?.labelText;
  const defaultRadioIndex =
    props.options.findIndex((option) => option.isDefault) >= 0
      ? props.options.findIndex((option) => option.isDefault)
      : 0;
  const [radioGroup, setRadioGroup] = useState<HTMLInputElement[]>();
  const [radioText, setRadioText] = useState(defaultText || '');
  const [radioIndex, setRadioIndex] = useState(defaultRadioIndex);
  const renderTabIndex = (targetIndex: number) => {
    if (radioGroup) {
      radioGroup.forEach((_, index) => {
        if (index === targetIndex) {
          radioGroup[index].tabIndex = 0;
        } else {
          radioGroup[index].tabIndex = -1;
        }
      });
    }
  };
  const confirmRadioButton = (radioIndex: number) => {
    if (radioGroup && radioIndex !== undefined) {
      const radioValue = (radioGroup[radioIndex] as HTMLInputElement)?.value;
      setRadioText(radioValue);
      props.onChange(radioValue);
      renderTabIndex(radioIndex);
    }
  };
  useEffect(() => {
    const radios = Array.from(document.querySelectorAll(`input[type="radio"]`));
    if (radios) {
      setRadioGroup(radios as HTMLInputElement[]);
    }
  }, []);

  useEffect(() => {
    /**
     * set the default radio tabIndex to 0 and the rest to -1 on page load
     */
    renderTabIndex(defaultRadioIndex);
  }, [defaultRadioIndex, radioGroup]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        if (radioGroup) {
          if (radioIndex + 1 < radioGroup.length) {
            setRadioIndex(radioIndex + 1);
            confirmRadioButton(radioIndex + 1);
          } else {
            setRadioIndex(0);
            confirmRadioButton(0);
          }
        }
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        if (radioGroup) {
          if (radioIndex - 1 >= 0) {
            setRadioIndex(radioIndex - 1);
            confirmRadioButton(radioIndex - 1);
          } else {
            setRadioIndex(radioGroup.length - 1);
            confirmRadioButton(radioGroup.length - 1);
          }
        }
        break;

      case 'Enter':
      case ' ':
        if (radioIndex !== undefined && radioIndex >= 0) {
          confirmRadioButton(radioIndex);
        } else {
          confirmRadioButton(0);
        }
        break;
    }
  };

  return (
    <form
      className={`mb-1 ${props.isLabelTextTitle ? 'mt-[2.75rem] flex flex-col gap-[1.5rem]' : ''}`}
    >
      <Label
        className={`${props.className} !text-brand-primary text-lg font-semibold`}
        id={props.title}
        htmlFor={props.title}
        text={props.title}
        isTitle={props.isLabelTextTitle}
      />
      {props.description ? (
        <p
          data-testid="description"
          className="text-text-body text-bodyLg font-normal leading-[28.5px]"
        >
          {props.description}
        </p>
      ) : null}
      <InlineErrorMessage
        path={props.errorPath}
        messages={props.errorMessages}
      />

      <fieldset className="mt-4 flex flex-col" role="radiogroup">
        {props.options.map((option, i) => (
          <SingleRadioButton
            key={option.labelText}
            id={option.id || option.labelText}
            name={option.name || option.labelText}
            labelText={option.labelText}
            subtext={option.subtext}
            buttonRef={option.buttonRef}
            radio={radioText}
            setRadio={setRadioText}
            onChange={props.onChange}
            onKeyDown={onKeyDown}
            radioIndex={i}
          />
        ))}
      </fieldset>
    </form>
  );
}

export default RadioOptions;
